import apiService from './api';
import { PaginationResponse } from './types';
import apiClient from '../utils/apiClient';

export enum StoreStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended'
}

export enum ApplicationStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected'
}

export interface Store {
  _id: string;
  /** business = فروشگاه، personal = فروشندهٔ شخصی (نام/لوگو از پروفایل کاربر) */
  kind?: 'business' | 'personal';
  previousSlugs?: string[];
  name: string;
  slug: string;
  description: string;
  logo: string;
  banner: string;
  email: string;
  phone: string;
  address?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  socialLinks?: {
    instagram?: string;
    telegram?: string;
    whatsapp?: string;
    website?: string;
  };
  categories: string[];
  status: StoreStatus;
  isVerified: boolean;
  verificationNotes?: string;
  lastStatusChange?: {
    changedBy: {
      _id: string;
      name: string;
      email: string;
    };
    changedAt: string;
    reason: string;
    previousStatus?: StoreStatus;
  };
  customCommission?: number | null;
  /** پلکان کمیسیون اختصاصی (حاشیه‌ای) — اولویت بالاتر از customCommission */
  customCommissionTiers?: Array<{ upTo: number | null; percent: number }> | null;
  /** کمیسیون مبلغ‌ثابت اختصاصی (تومان به‌ازای هر برداشت) */
  customCommissionFixed?: number | null;
  /** پلکان مبلغ‌ثابت اختصاصی (bracket — کل مبلغ در پله‌اش) */
  customCommissionFixedTiers?: Array<{ upTo: number | null; amount: number }> | null;
  commissionNotes?: string;
  policies?: {
    returns?: string;
    shipping?: string;
    additionalInfo?: string;
  };
  rating: {
    average: number;
    count: number;
  };
  userId: string | {
    _id: string;
    name: string;
    email: string;
  };
  statusChangeHistory?: Array<{
    changedBy: string;
    changedAt: string;
    reason: string;
    fromStatus: string;
    toStatus: string;
    _id?: string;
  }>;
  commissionChangeHistory?: Array<{
    changedBy: string;
    changedAt: string;
    reason: string;
    fromCommission: number | null;
    toCommission: number | null;
    fromTiers?: Array<{ upTo: number | null; percent: number }> | null;
    toTiers?: Array<{ upTo: number | null; percent: number }> | null;
    _id?: string;
  }>;
  verificationChangeHistory?: Array<{
    changedBy: string;
    changedAt: string;
    reason: string;
    fromVerified: boolean;
    toVerified: boolean;
    _id?: string;
  }>;
  // حوزه‌های فعالیت فروشگاه
  activityAreas?: ActivityAreaInfo[];
  createdAt: string;
  updatedAt: string;
}

// اینترفیس حوزه فعالیت برای نمایش
export interface ActivityAreaInfo {
  _id: string;
  name: string;
  description?: string;
  icon?: string;
  isActive: boolean;
  hasPostalDelivery: boolean;
}

export interface StoreApplication {
  _id: string;
  userId: string;
  /** new = فروشگاه جدید، conversion = تبدیل حساب فروشندهٔ شخصی به فروشگاه (سند Store موجود درجا business می‌شود) */
  applicationKind?: 'new' | 'conversion';
  conversionOfStoreId?: string;
  storeName: string;
  storeEmail: string;
  storePhone?: string;
  ownerFirstName?: string;
  ownerLastName?: string;
  nationalId?: string;
  businessInfo?: string;
  status: ApplicationStatus;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  storeImage?: string;
  storeLogoUrl?: string;
  // حوزه‌های فعالیت فروشگاه
  activityAreas?: ActivityAreaInfo[];
}

export interface StoreFilters {
  status?: StoreStatus;
  kind?: 'business' | 'personal';
  name?: string;
  category?: string;
  minRating?: number;
  verificationStatus?: boolean;
}

export interface ApplicationFilters {
  status?: ApplicationStatus;
  storeName?: string;
  startDate?: string;
  endDate?: string;
}

export interface StoreDetail {
  store: Store;
  sellerApplication: StoreApplication | null;
}

export interface AdminStoreInfo {
  _id: string;
  id?: string; // For backward compatibility
  userId: string;
  name: string;
  slug?: string;
  logo?: string;
  banner?: string;
  description?: string;
  email: string;
  phone: string;
  address?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  categories: string[];
  socialLinks?: {
    website?: string;
    instagram?: string;
    telegram?: string;
    whatsapp?: string;
  };
  bankInfo?: {
    accountNumber: string;
    iban: string;
    bankName: string;
    accountHolder: string;
  };
  status: 'pending' | 'active' | 'suspended' | 'rejected' | 'inactive';
  isVerified: boolean;
  verificationLevel?: 'none' | 'basic' | 'premium';
  rating: {
    average: number;
    count: number;
    breakdown?: {
      1: number;
      2: number;
      3: number;
      4: number;
      5: number;
    };
  };
  metrics?: {
    totalProducts: number;
    totalOrders: number;
    totalRevenue: number;
    monthlyRevenue: number;
    totalCustomers: number;
    averageOrderValue: number;
  };
  owner?: {
    id: string;
    name: string;
    email: string;
    phone: string;
    profileImage?: string;
    nationalId?: string;
    birthDate?: string;
    registrationDate: string;
  };
  settings?: {
    commissionRate: number;
    maxProducts: number;
    allowedCategories: string[];
    features: string[];
  };
  // History fields
  lastStatusChange?: {
    changedBy: string;
    changedAt: string;
    reason: string;
    previousStatus?: string;
  };
  customCommission?: number | null;
  /** پلکان کمیسیون اختصاصی (حاشیه‌ای) — اولویت بالاتر از customCommission */
  customCommissionTiers?: Array<{ upTo: number | null; percent: number }> | null;
  /** کمیسیون مبلغ‌ثابت اختصاصی (تومان به‌ازای هر برداشت) */
  customCommissionFixed?: number | null;
  /** پلکان مبلغ‌ثابت اختصاصی (bracket — کل مبلغ در پله‌اش) */
  customCommissionFixedTiers?: Array<{ upTo: number | null; amount: number }> | null;
  commissionNotes?: string;
  verificationNotes?: string;
  statusChangeHistory?: Array<{
    changedBy: string;
    changedAt: string;
    reason: string;
    fromStatus: string;
    toStatus: string;
    _id?: string;
  }>;
  commissionChangeHistory?: Array<{
    changedBy: string;
    changedAt: string;
    reason: string;
    fromCommission: number | null;
    toCommission: number | null;
    fromTiers?: Array<{ upTo: number | null; percent: number }> | null;
    toTiers?: Array<{ upTo: number | null; percent: number }> | null;
    _id?: string;
  }>;
  verificationChangeHistory?: Array<{
    changedBy: string;
    changedAt: string;
    reason: string;
    fromVerified: boolean;
    toVerified: boolean;
    _id?: string;
  }>;
  editPending?: boolean;
  __v?: number;
  createdAt: string;
  updatedAt: string;
  lastActivity?: string;
}

export interface StoreSearchFilters {
  query?: string;
  status?: string;
  verificationLevel?: string;
  city?: string;
  province?: string;
  minRating?: number;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface StoreUpdateData {
  name?: string;
  description?: string;
  status?: string;
  isVerified?: boolean;
  verificationLevel?: string;
  settings?: {
    commissionRate?: number;
    maxProducts?: number;
    allowedCategories?: string[];
    features?: string[];
  };
}

export interface StoreActionLog {
  id: string;
  storeId: string;
  action: string;
  description: string;
  adminId: string;
  adminName: string;
  timestamp: string;
  oldValues?: any;
  newValues?: any;
}

class AdminStoreService {
  /**
   * Get all stores with filters and pagination
   */
  async getStores(page: number = 1, limit: number = 10, filters: any = {}): Promise<{
    items: AdminStoreInfo[];
    meta: {
      totalItems: number;
      totalPages: number;
      currentPage: number;
      itemsPerPage: number;
    };
  }> {
    try {
      console.log('🏪 Fetching stores with filters:', filters);
      
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await apiClient.get(`/admin/stores?${params.toString()}`);
      
      if (response.data.success) {
        console.log('✅ Stores fetched successfully:', response.data.data);
        
        // Handle the actual API response structure
        const data = response.data.data;
        const stores = data.docs || [];
        
        return {
          items: stores,
          meta: {
            totalItems: data.totalDocs || 0,
            totalPages: data.totalPages || 0,
            currentPage: data.page || page,
            itemsPerPage: data.limit || limit
          }
        };
      } else {
        throw new Error(response.data.message || 'خطا در دریافت فروشگاه‌ها');
      }
    } catch (error: any) {
      console.error('❌ Error fetching stores:', error);
      throw new Error(error.response?.data?.message || 'خطا در دریافت فروشگاه‌ها');
    }
  }

  /**
   * Get single store by ID
   */
  async getStoreById(storeId: string): Promise<AdminStoreInfo> {
    try {
      console.log(`🏪 Fetching store details for ID: ${storeId}`);
      
      const response = await apiClient.get(`/admin/stores/${storeId}`);
      
      if (response.data.success) {
        console.log('✅ Store details fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'خطا در دریافت اطلاعات فروشگاه');
      }
    } catch (error: any) {
      console.error('❌ Error fetching store details:', error);
      
      if (error.response?.status === 404) {
        throw new Error('فروشگاه یافت نشد');
      } else if (error.response?.status === 403) {
        throw new Error('دسترسی غیرمجاز');
      } else {
        throw new Error(error.response?.data?.message || 'خطا در دریافت اطلاعات فروشگاه');
      }
    }
  }

  /**
   * Update store information
   */
  async updateStore(storeId: string, updateData: any): Promise<AdminStoreInfo> {
    try {
      console.log(`🔄 Updating store ${storeId} with data:`, updateData);
      
      const response = await apiClient.put(`/admin/stores/${storeId}`, updateData);
      
      if (response.data.success) {
        console.log('✅ Store updated successfully:', response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'خطا در به‌روزرسانی فروشگاه');
      }
    } catch (error: any) {
      console.error('❌ Error updating store:', error);
      throw new Error(error.response?.data?.message || 'خطا در به‌روزرسانی فروشگاه');
    }
  }

  /**
   * Change store status
   */
  async changeStoreStatus(storeId: string, status: string, reason?: string): Promise<AdminStoreInfo> {
    try {
      console.log(`🔄 Changing store ${storeId} status to: ${status}`);
      
      const response = await apiClient.patch(`/admin/stores/${storeId}/status`, {
        status,
        reason
      });
      
      if (response.data.success) {
        console.log('✅ Store status changed successfully:', response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'خطا در تغییر وضعیت فروشگاه');
      }
    } catch (error: any) {
      console.error('❌ Error changing store status:', error);
      throw new Error(error.response?.data?.message || 'خطا در تغییر وضعیت فروشگاه');
    }
  }

  /**
   * Verify store
   */
  async verifyStore(storeId: string, verificationLevel: string): Promise<AdminStoreInfo> {
    try {
      console.log(`✅ Verifying store ${storeId} with level: ${verificationLevel}`);
      
      const response = await apiClient.patch(`/admin/stores/${storeId}/verify`, {
        verificationLevel
      });
      
      if (response.data.success) {
        console.log('✅ Store verified successfully:', response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'خطا در تایید فروشگاه');
      }
    } catch (error: any) {
      console.error('❌ Error verifying store:', error);
      throw new Error(error.response?.data?.message || 'خطا در تایید فروشگاه');
    }
  }

  /**
   * Get store action logs
   */
  async getStoreActionLogs(storeId: string, page: number = 1, limit: number = 20): Promise<{
    logs: StoreActionLog[];
    pagination: any;
  }> {
    try {
      console.log(`📋 Fetching action logs for store: ${storeId}`);
      
      const response = await apiClient.get(`/admin/stores/${storeId}/logs?page=${page}&limit=${limit}`);
      
      if (response.data.success) {
        console.log('✅ Action logs fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'خطا در دریافت تاریخچه عملیات');
      }
    } catch (error: any) {
      console.error('❌ Error fetching action logs:', error);
      throw new Error(error.response?.data?.message || 'خطا در دریافت تاریخچه عملیات');
    }
  }

  /**
   * Delete store
   */
  async deleteStore(storeId: string, reason: string): Promise<void> {
    try {
      console.log(`🗑️ Deleting store: ${storeId}, reason: ${reason}`);
      
      const response = await apiClient.delete(`/admin/stores/${storeId}`, {
        data: { reason }
      });
      
      if (response.data.success) {
        console.log('✅ Store deleted successfully');
      } else {
        throw new Error(response.data.message || 'خطا در حذف فروشگاه');
      }
    } catch (error: any) {
      console.error('❌ Error deleting store:', error);
      throw new Error(error.response?.data?.message || 'خطا در حذف فروشگاه');
    }
  }

  /**
   * Get store analytics
   */
  async getStoreAnalytics(storeId: string, period: string = '30d'): Promise<{
    revenue: any[];
    orders: any[];
    customers: any[];
    products: any[];
    summary: {
      totalRevenue: number;
      totalOrders: number;
      totalCustomers: number;
      averageOrderValue: number;
      growthRate: number;
    };
  }> {
    try {
      console.log(`📊 Fetching analytics for store: ${storeId}, period: ${period}`);
      
      const response = await apiClient.get(`/admin/stores/${storeId}/analytics?period=${period}`);
      
      if (response.data.success) {
        console.log('✅ Analytics fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'خطا در دریافت آمار فروشگاه');
      }
    } catch (error: any) {
      console.error('❌ Error fetching store analytics:', error);
      throw new Error(error.response?.data?.message || 'خطا در دریافت آمار فروشگاه');
    }
  }

  /**
   * Get store applications with filters and pagination
   */
  async getStoreApplications(page: number = 1, limit: number = 10, filters: ApplicationFilters = {}): Promise<{
    items: StoreApplication[];
    meta: {
      totalItems: number;
      totalPages: number;
      currentPage: number;
      itemsPerPage: number;
    };
  }> {
    try {
      console.log('📋 Fetching store applications with filters:', filters);
      
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await apiClient.get(`/admin/sellers/applications?${params.toString()}`);
      
      if (response.data.success) {
        console.log('✅ Store applications fetched successfully:', response.data);
        
        // Handle the actual API response structure
        const applications = response.data.data || [];
        const pagination = response.data.pagination || {};
        
        return {
          items: applications,
          meta: {
            totalItems: pagination.total || 0,
            totalPages: pagination.pages || 0,
            currentPage: pagination.page || page,
            itemsPerPage: pagination.limit || limit
          }
        };
      } else {
        throw new Error(response.data.message || 'خطا در دریافت درخواست‌های فروشندگی');
      }
    } catch (error: any) {
      console.error('❌ Error fetching store applications:', error);
      throw new Error(error.response?.data?.message || 'خطا در دریافت درخواست‌های فروشندگی');
    }
  }

  /**
   * Approve store application
   */
  async approveStoreApplication(applicationId: string, notes?: string): Promise<StoreApplication> {
    try {
      console.log(`✅ Approving store application: ${applicationId}`);
      
      const response = await apiClient.post(`/admin/sellers/applications/${applicationId}/approve`, {
        verificationNotes: notes
      });
      
      if (response.data.success) {
        console.log('✅ Store application approved successfully:', response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'خطا در تایید درخواست فروشندگی');
      }
    } catch (error: any) {
      console.error('❌ Error approving store application:', error);
      throw new Error(error.response?.data?.message || 'خطا در تایید درخواست فروشندگی');
    }
  }

  /**
   * Reject store application
   */
  async rejectStoreApplication(applicationId: string, rejectionReason: string): Promise<StoreApplication> {
    try {
      console.log(`❌ Rejecting store application: ${applicationId}`);
      
      const response = await apiClient.post(`/admin/sellers/applications/${applicationId}/reject`, {
        rejectionReason
      });
      
      if (response.data.success) {
        console.log('✅ Store application rejected successfully:', response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'خطا در رد درخواست فروشندگی');
      }
    } catch (error: any) {
      console.error('❌ Error rejecting store application:', error);
      throw new Error(error.response?.data?.message || 'خطا در رد درخواست فروشندگی');
    }
  }

  /**
   * Update store status
   */
  async updateStoreStatus(storeId: string, status: string, reason?: string): Promise<AdminStoreInfo> {
    try {
      console.log(`🔄 Updating store ${storeId} status to: ${status}`);
      
      const response = await apiClient.patch(`/admin/stores/${storeId}/status`, {
        status,
        reason
      });
      
      if (response.data.success) {
        console.log('✅ Store status updated successfully:', response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'خطا در تغییر وضعیت فروشگاه');
      }
    } catch (error: any) {
      console.error('❌ Error updating store status:', error);
      throw new Error(error.response?.data?.message || 'خطا در تغییر وضعیت فروشگاه');
    }
  }

  /**
   * Update store verification status
   */
  async updateVerificationStatus(storeId: string, isVerified: boolean, notes?: string): Promise<AdminStoreInfo> {
    try {
      console.log(`✅ Updating store ${storeId} verification to: ${isVerified}`);
      
      const response = await apiClient.patch(`/admin/stores/${storeId}/verification`, {
        isVerified,
        notes
      });
      
      if (response.data.success) {
        console.log('✅ Store verification updated successfully:', response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'خطا در تغییر وضعیت تایید فروشگاه');
      }
    } catch (error: any) {
      console.error('❌ Error updating store verification:', error);
      throw new Error(error.response?.data?.message || 'خطا در تغییر وضعیت تایید فروشگاه');
    }
  }

  /**
   * Update store commission
   */
  async updateStoreCommission(
    storeId: string,
    commission: number | null,
    notes?: string,
    commissionTiers?: Array<{ upTo: number | null; percent: number }> | null,
    commissionFixed?: number | null,
    commissionFixedTiers?: Array<{ upTo: number | null; amount: number }> | null
  ): Promise<AdminStoreInfo> {
    try {
      console.log(`💰 Updating store ${storeId} commission to: ${commission}`, commissionTiers, commissionFixed, commissionFixedTiers);

      const response = await apiClient.patch(`/admin/stores/${storeId}/commission`, {
        customCommission: commission,
        commissionNotes: notes,
        // undefined = دست‌نخورده؛ null/[] = حذف پلکان؛ آرایه = تنظیم پلکان
        ...(commissionTiers !== undefined ? { customCommissionTiers: commissionTiers } : {}),
        // حالت‌های مبلغ‌ثابت (2026-08-16) — همان قرارداد undefined/null/مقدار
        ...(commissionFixed !== undefined ? { customCommissionFixed: commissionFixed } : {}),
        ...(commissionFixedTiers !== undefined ? { customCommissionFixedTiers: commissionFixedTiers } : {})
      });
      
      if (response.data.success) {
        console.log('✅ Store commission updated successfully:', response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'خطا در تغییر کمیسیون فروشگاه');
      }
    } catch (error: any) {
      console.error('❌ Error updating store commission:', error);
      throw new Error(error.response?.data?.message || 'خطا در تغییر کمیسیون فروشگاه');
    }
  }

  /**
   * Get status options
   */
  getStatusOptions() {
    return [
      { value: 'pending', label: 'در انتظار بررسی', color: '#ff9800' },
      { value: 'active', label: 'فعال', color: '#4caf50' },
      { value: 'suspended', label: 'تعلیق شده', color: '#f44336' },
      { value: 'rejected', label: 'رد شده', color: '#9e9e9e' }
    ];
  }

  /**
   * Get verification level options
   */
  getVerificationLevelOptions() {
    return [
      { value: 'none', label: 'تایید نشده', color: '#9e9e9e' },
      { value: 'basic', label: 'تایید پایه', color: '#2196f3' },
      { value: 'premium', label: 'تایید ویژه', color: '#ff9800' }
    ];
  }

  /**
   * Format store metrics for display
   */
  formatMetrics(metrics: AdminStoreInfo['metrics']) {
    if (!metrics) {
      return {
        totalRevenue: '0 تومان',
        monthlyRevenue: '0 تومان',
        totalOrders: '0',
        totalProducts: '0',
        totalCustomers: '0',
        averageOrderValue: '0 تومان'
      };
    }
    
    return {
      totalRevenue: new Intl.NumberFormat('fa-IR').format(metrics.totalRevenue) + ' تومان',
      monthlyRevenue: new Intl.NumberFormat('fa-IR').format(metrics.monthlyRevenue) + ' تومان',
      totalOrders: new Intl.NumberFormat('fa-IR').format(metrics.totalOrders),
      totalProducts: new Intl.NumberFormat('fa-IR').format(metrics.totalProducts),
      totalCustomers: new Intl.NumberFormat('fa-IR').format(metrics.totalCustomers),
      averageOrderValue: new Intl.NumberFormat('fa-IR').format(metrics.averageOrderValue) + ' تومان'
    };
  }

  /**
   * Get status display
   */
  getStatusDisplay(status: string) {
    const statusMap: { [key: string]: { label: string; color: string } } = {
      pending: { label: 'در انتظار بررسی', color: '#ff9800' },
      active: { label: 'فعال', color: '#4caf50' },
      suspended: { label: 'تعلیق شده', color: '#f44336' },
      rejected: { label: 'رد شده', color: '#9e9e9e' }
    };
    
    return statusMap[status] || { label: status, color: '#9e9e9e' };
  }

  /**
   * Get verification display
   */
  getVerificationDisplay(level: string) {
    const levelMap: { [key: string]: { label: string; color: string } } = {
      none: { label: 'تایید نشده', color: '#9e9e9e' },
      basic: { label: 'تایید پایه', color: '#2196f3' },
      premium: { label: 'تایید ویژه', color: '#ff9800' }
    };
    
    return levelMap[level] || { label: level, color: '#9e9e9e' };
  }
}

export default new AdminStoreService(); 